import { Card, Space, Tag, Typography } from "antd";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { stripMarkdown } from "../cardUtils";
import { CustomTooltip } from "src/components/CustomTooltip";

/**
 * InsightCard Component
 *
 * A card component for displaying insights with type, impact, description, and supporting data.
 * This component is designed to highlight key insights with their associated metrics and evidence.
 *
 * @example
 * ```tsx
 * <InsightCard
 *   type="trend"
 *   impactType="velocity"
 *   description="Terraform PRs increased by 3x, with 2 critical bugs resolved."
 *   contributors={["John Doe", "Jane Smith"]}
 *   evidence={[
 *     "PR #1234: Infrastructure updates",
 *     "Issue #567: Bug fixes"
 *   ]}
 *   change="+200% PR volume"
 * />
 * ```
 */
export interface InsightItemProps {
  /**
   * The type of insight (e.g., 'trend', 'shift', 'anomaly', 'pattern', 'focus')
   */
  type: string;
  description: string;
  evidence: string[];
  change: string;
  className?: string;
  variant?: "default" | "summary" | "minimal";
}

export const InsightItem: React.FC<InsightItemProps> = ({
  type,
  description,
  evidence,
  change,
  className,
  variant = "default",
}) => {
  if (variant === "summary") {
    // Summary card: just main insight and type
    return (
      <Card
        className={[className, "vzbl-insight-summary"]
          .filter(Boolean)
          .join(" ")}
        size="small"
      >
        <div style={{ fontWeight: 600 }}>{type}</div>
        <div>
          <Markdown remarkPlugins={[remarkGfm]} skipHtml={true}>
            {description}
          </Markdown>
        </div>
      </Card>
    );
  }
  if (variant === "minimal") {
    // Minimal, text-only card
    return (
      <div
        className={[className, "vzbl-insight-minimal"]
          .filter(Boolean)
          .join(" ")}
        style={{ padding: 8, fontSize: 14 }}
      >
        <strong>{type}</strong>
        <Markdown remarkPlugins={[remarkGfm]} skipHtml={true}>
          {description}
        </Markdown>
      </div>
    );
  }
  // Default full card
  return (
    <Card
      className={[className, "vzbl-insight-default"].filter(Boolean).join(" ")}
      size="small"
      style={{
        border: "solid 1px var(--color-foreground-quaternary)",
        borderRadius: "var(--border-radius-2)",
      }}
    >
      <Space direction="vertical" size="small" style={{ width: "100%" }}>
        <Space size={8} wrap style={{ flexWrap: "wrap", maxWidth: "100%" }}>
          <Tag className="vzbl-pill-sm">{type}</Tag>
          <Tag className="vzbl-pill-sm">{change}</Tag>
        </Space>

        <Typography.Text className="vzbl-text-3">
          <Markdown remarkPlugins={[remarkGfm]} skipHtml={true}>
            {description}
          </Markdown>
        </Typography.Text>

        {evidence?.length > 0 && (
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {evidence.map((item, idx) => (
              <li key={idx}>
                <Markdown
                  remarkPlugins={[remarkGfm]}
                  skipHtml={true}
                  className="vzbl-text-3"
                >
                  {item}
                </Markdown>
              </li>
            ))}
          </ul>
        )}
      </Space>
    </Card>
  );
};
